import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { api } from "../api/client";
import { AUTOPILOT_MANUAL_OPEN_BLOCKED_REASON } from "../components/AutopilotProvider";
import { useAutopilot } from "../components/autopilotContext";
import { TokenPair, TokenPairName } from "../components/TokenPair";
import { priceFrame } from "../lib/priceFrame";
import { dexName, dexPoolUrl, openableReason } from "../lib/dex";
import { PoolDetails } from "../components/PoolDetails";
import { DexBadge } from "../components/DexBadge";
import { CatalogControls } from "../components/CatalogControls";
import { PriceTrend } from "../components/PriceTrend";
import { RefreshButton } from "../components/RefreshButton";
import { EffectiveLpsCell, EffectiveLpsCoordinator } from "../components/EffectiveLpsCell";
import { useRefreshPhase } from "../lib/useRefreshPhase";
import { formatAgo, formatMoney, formatPercent, formatPrice, shorten } from "../lib/format";
import {
  columnOptions,
  defaultCatalogColumns,
  readCatalogPreferences,
  writeCatalogPreferences,
  type CatalogColumn,
  type CatalogFilters,
} from "../lib/catalog-preferences";
import type { PoolTrendsResponse } from "@auto-pools/core";
import type { Period, Pool, PoolSort, PoolsResponse, VolumePeriod, YieldPeriod } from "../types";

const TRENDS_INTERVAL_MS = 60_000;
const MAX_TREND_POOLS = 500;

function clearTrendInterest(viewer: string) {
  api<unknown>("/pools/trends", {
    method: "POST",
    body: { viewer, pools: [] },
    keepalive: true,
  }).catch(() => undefined);
}

interface PoolsSnapshot {
  queryIdentity: string;
  response: PoolsResponse;
}

const yieldPeriods: YieldPeriod[] = ["15m", "30m", "1h", "4h", "8h", "24h", "7d", "30d", "365d"];
const volumePeriods: VolumePeriod[] = ["5m", "15m", "30m", "1h", "2h", "4h", "8h", "24h", "7d"];
const columnWidths: Record<CatalogColumn, number> = {
  priceTrend: 128,
  tokenA: 222,
  tokenB: 222,
  yieldTvl: 140,
  volume: 132,
  tvl: 112,
  fees: 128,
  effectiveLps: 128,
  rewards: 128,
  price: 160,
  address: 220,
};

const poolColumnWidth = 260;
const openColumnWidth = 82;

function withTrend(pool: Pool, trends: PoolTrendsResponse["trends"]): Pool {
  const trend = pool.dex === "raydium" ? trends[pool.address] : undefined;
  return trend ? { ...pool, priceHistory7d: trend.samples } : pool;
}

function volumeLabel(period: string) {
  return period.endsWith("m") ? period : period.toUpperCase();
}

export function PoolsPage({
  refresh,
  visible = true,
  onOpen = () => undefined,
}: {
  refresh: number;
  visible?: boolean;
  onOpen?: (pool: Pool, referenceAt: string | undefined, origin: HTMLButtonElement) => void;
}) {
  void refresh; // Server events refresh operational pages, never the catalog.
  const [initialPreferences] = useState(readCatalogPreferences);
  const [period, setPeriod] = useState<Period>(initialPreferences.period);
  const [volumePeriod, setVolumePeriod] = useState<VolumePeriod>(initialPreferences.volumePeriod);
  const [yieldPeriod, setYieldPeriod] = useState<YieldPeriod>(initialPreferences.yieldPeriod);
  const [sort, setSort] = useState<PoolSort>(initialPreferences.sort);
  const [direction, setDirection] = useState<"asc" | "desc">(initialPreferences.direction);
  const [draftSearch, setDraftSearch] = useState(initialPreferences.appliedSearch);
  const [search, setSearch] = useState(initialPreferences.appliedSearch);
  const [filters, setFilters] = useState<CatalogFilters>(initialPreferences.appliedFilters);
  const [visibleColumns, setVisibleColumns] = useState<Record<CatalogColumn, boolean>>(
    () =>
      Object.fromEntries(
        columnOptions.map(({ value }) => [
          value,
          initialPreferences.enabledColumns.includes(value),
        ]),
      ) as Record<CatalogColumn, boolean>,
  );
  const [snapshot, setSnapshot] = useState<PoolsSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [freshnessFailed, setFreshnessFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [moreLoading, setMoreLoading] = useState(false);
  const [request, setRequest] = useState(0);
  const [effectiveLpsGeneration, setEffectiveLpsGeneration] = useState(0);
  const [effectiveLpsCoordinator] = useState(() => new EffectiveLpsCoordinator());
  const manualRefresh = useRef(false);
  const [now, setNow] = useState(Date.now);
  const moreRequest = useRef<AbortController | null>(null);

  useEffect(() => effectiveLpsCoordinator.lease(), [effectiveLpsCoordinator]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 1_000);
    return () => {
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    writeCatalogPreferences({
      period,
      volumePeriod,
      yieldPeriod,
      sort,
      direction,
      enabledColumns: columnOptions
        .filter(({ value }) => visibleColumns[value])
        .map(({ value }) => value),
      appliedSearch: search,
      appliedFilters: filters,
    });
  }, [period, volumePeriod, yieldPeriod, sort, direction, visibleColumns, search, filters]);

  const params = useMemo(() => {
    const value = new URLSearchParams({ period, volumePeriod, yieldPeriod, sort, direction });
    if (search) value.set("search", search);
    if (filters.minTvl) value.set("minTvl", filters.minTvl);
    if (filters.minVolume) value.set("minVolume", filters.minVolume);
    if (filters.withoutMemes) value.set("withoutMemes", "true");
    if (filters.hasRewards) value.set("hasRewards", "true");
    if (filters.noWarnings) value.set("noWarnings", "true");
    if (filters.hasAdaptiveFee) value.set("hasAdaptiveFee", "true");
    if (filters.isWavebreak) value.set("isWavebreak", "true");
    if (filters.hasLockedLiquidity) {
      value.set("hasLockedLiquidity", "true");
      value.set("minLockedLiquidityPercent", filters.minLockedLiquidityPercent);
    }
    return value;
  }, [period, volumePeriod, yieldPeriod, sort, direction, search, filters]);
  const queryIdentity = params.toString();
  const result = snapshot?.queryIdentity === queryIdentity ? snapshot.response : null;
  const freshnessTone = result
    ? freshnessFailed || result.stale
      ? "warn"
      : "good"
    : freshnessFailed
      ? "bad"
      : "";
  // A cached snapshot means the catalog refresh did not reach its source: not an update.
  const refreshButton = useRefreshPhase({
    pending: loading,
    failed: freshnessFailed || result?.stale === true,
  });
  const refreshConfirmed =
    refreshButton.phase === "done" && result !== null && !freshnessFailed && !result.stale;
  const freshnessText = result
    ? freshnessFailed
      ? `${result.stale ? "Last known cached snapshot " : "Last known update "}${formatAgo(result.updatedAt, now)} · refresh failed`
      : refreshConfirmed
        ? "Updated just now"
        : `${result.stale ? "Cached snapshot " : "Updated "}${formatAgo(result.updatedAt, now)}`
    : loading
      ? "Loading catalog…"
      : "Freshness unavailable";
  const queryIdentityRef = useRef(queryIdentity);
  queryIdentityRef.current = queryIdentity;
  const displayedColumns = columnOptions.filter((column) => visibleColumns[column.value]);
  const [trendViewer] = useState(() => crypto.randomUUID());
  const [documentVisible, setDocumentVisible] = useState(
    () => document.visibilityState === "visible",
  );
  const [trends, setTrends] = useState<PoolTrendsResponse["trends"]>({});
  const trendsPosted = useRef(false);
  const trendsActive = visible && visibleColumns.priceTrend && documentVisible;
  const raydiumPools = useMemo(() => {
    const addresses = new Set<string>();
    for (const pool of result?.items ?? []) {
      if (pool.dex === "raydium") addresses.add(pool.address);
    }
    return [...addresses].slice(0, MAX_TREND_POOLS);
  }, [result]);
  const columnCount = displayedColumns.length + 2;

  useEffect(() => {
    const controller = new AbortController();
    moreRequest.current?.abort();
    setMoreLoading(false);
    setLoading(true);
    const requestParams = new URLSearchParams(params);
    if (manualRefresh.current) requestParams.set("refresh", "true");
    api<PoolsResponse>(`/pools?${requestParams}`, { signal: controller.signal }).then(
      (data) => {
        if (!controller.signal.aborted) {
          setSnapshot({ queryIdentity, response: data });
          setError(null);
          setFreshnessFailed(false);
          setLoading(false);
          manualRefresh.current = false;
        }
      },
      (reason: unknown) => {
        if (!controller.signal.aborted) {
          setError(reason instanceof Error ? reason.message : "Catalog unavailable");
          setFreshnessFailed(true);
          setLoading(false);
          manualRefresh.current = false;
        }
      },
    );
    return () => {
      controller.abort();
      moreRequest.current?.abort();
    };
  }, [params, queryIdentity, request]);

  useEffect(() => {
    const onVisibility = () => {
      setDocumentVisible(document.visibilityState === "visible");
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  useEffect(() => {
    if (!trendsActive) return;
    if (raydiumPools.length === 0) {
      if (trendsPosted.current) {
        trendsPosted.current = false;
        clearTrendInterest(trendViewer);
      }
      return;
    }
    let controller: AbortController | null = null;
    const post = () => {
      controller?.abort();
      const own = new AbortController();
      controller = own;
      trendsPosted.current = true;
      api<PoolTrendsResponse>("/pools/trends", {
        method: "POST",
        body: { viewer: trendViewer, pools: raydiumPools },
        signal: own.signal,
      }).then(
        (data) => {
          if (!own.signal.aborted) setTrends(data.trends);
        },
        () => undefined, // Display-only: rows stay as they are and the next tick retries.
      );
    };
    post();
    const timer = window.setInterval(post, TRENDS_INTERVAL_MS);
    return () => {
      window.clearInterval(timer);
      controller?.abort();
    };
  }, [trendsActive, raydiumPools, trendViewer]);

  // Separate from the poll effect so a new snapshot re-posts without clearing in between.
  useEffect(() => {
    if (!trendsActive) return;
    return () => {
      if (!trendsPosted.current) return;
      trendsPosted.current = false;
      clearTrendInterest(trendViewer);
    };
  }, [trendsActive, trendViewer]);

  function submitSearch(event: FormEvent) {
    event.preventDefault();
    setSearch(draftSearch.trim());
  }

  function changeSort(value: PoolSort) {
    if (sort === value) setDirection((current) => (current === "desc" ? "asc" : "desc"));
    else {
      setSort(value);
      setDirection("desc");
    }
  }

  function changeYieldPeriod(value: YieldPeriod) {
    setYieldPeriod(value);
    if (sort !== "yieldTvl") {
      setSort("yieldTvl");
      setDirection("desc");
    }
  }

  function removeFilter(filter: keyof CatalogFilters) {
    if (filter === "minTvl" || filter === "minVolume") {
      setFilters((current) => ({ ...current, [filter]: "" }));
      return;
    }
    if (filter === "minLockedLiquidityPercent") return;
    setFilters((current) => ({ ...current, [filter]: false }));
  }

  async function loadMore() {
    if (!result?.nextCursor) return;
    moreRequest.current?.abort();
    const controller = new AbortController();
    moreRequest.current = controller;
    const requestIdentity = queryIdentity;
    const requestedCursor = result.nextCursor;
    setMoreLoading(true);
    setError(null);
    try {
      const nextParams = new URLSearchParams(params);
      nextParams.set("cursor", result.nextCursor);
      const next = await api<PoolsResponse>(`/pools?${nextParams}`, {
        signal: controller.signal,
      });
      if (!controller.signal.aborted && queryIdentityRef.current === requestIdentity) {
        setSnapshot((current) =>
          current?.queryIdentity === requestIdentity &&
          current.response.nextCursor === requestedCursor
            ? {
                queryIdentity: requestIdentity,
                response: {
                  ...next,
                  items: [...current.response.items, ...next.items],
                },
              }
            : current,
        );
      }
    } catch (reason) {
      if (!controller.signal.aborted)
        setError(reason instanceof Error ? reason.message : "Could not load the next page");
    } finally {
      if (!controller.signal.aborted) setMoreLoading(false);
      if (moreRequest.current === controller) moreRequest.current = null;
    }
  }

  if (!visible) return null;

  return (
    <>
      <section className="pool-toolbar" aria-label="Pool catalog controls">
        <form className="search" onSubmit={submitSearch} role="search">
          <label className="sr-only" htmlFor="pool-search">
            Pair, mint, or pool address
          </label>
          <svg className="search-icon" viewBox="0 0 16 16" aria-hidden="true">
            <circle cx="7" cy="7" r="4.75" />
            <path d="m10.5 10.5 3 3" />
          </svg>
          <input
            id="pool-search"
            placeholder="Pair, mint, or pool address"
            maxLength={100}
            value={draftSearch}
            onChange={(event) => {
              setDraftSearch(event.target.value);
            }}
          />
          <kbd aria-hidden="true">/</kbd>
          <button className="sr-only">Search pools</button>
        </form>
        <div className="segmented" aria-label="Fees and rewards period">
          {(["24h", "4h"] as const).map((value) => (
            <button
              key={value}
              className={period === value ? "active" : ""}
              onClick={() => {
                setPeriod(value);
              }}
            >
              {value.toUpperCase()}
            </button>
          ))}
        </div>
        <CatalogControls
          filters={filters}
          columns={visibleColumns}
          sort={sort}
          direction={direction}
          volumePeriod={volumePeriod}
          yieldPeriod={yieldPeriod}
          onApplyFilters={setFilters}
          onColumnsChange={setVisibleColumns}
          onResetColumns={() => {
            setVisibleColumns(defaultCatalogColumns);
          }}
          onSortChange={(value) => {
            if (value === sort) return;
            setSort(value);
            setDirection("desc");
          }}
          onDirectionChange={() => {
            setDirection((value) => (value === "desc" ? "asc" : "desc"));
          }}
          onVolumePeriodChange={setVolumePeriod}
          onYieldPeriodChange={changeYieldPeriod}
        />
        <AppliedFilters filters={filters} onRemove={removeFilter} />
        <div className="catalog-freshness">
          <span className={`status-dot ${freshnessTone}`.trim()} />
          <span className={refreshConfirmed ? "refresh-confirmed" : undefined}>
            {freshnessText}
          </span>
        </div>
        <RefreshButton
          phase={refreshButton.phase}
          ariaLabel="Refresh"
          title="Refresh catalog"
          onClick={() => {
            if (!refreshButton.start()) return;
            manualRefresh.current = true;
            setEffectiveLpsGeneration((value) => value + 1);
            setRequest((value) => value + 1);
          }}
        />
      </section>
      {error && (
        <div className="catalog-message">
          <div className="inline-alert error" role="alert">
            {error}
            <button
              onClick={() => {
                setRequest((value) => value + 1);
              }}
            >
              Retry
            </button>
          </div>
        </div>
      )}
      <section className="panel table-panel" aria-busy={loading}>
        <div className="table-scroll">
          <table
            className="pools-table"
            style={{
              minWidth: Math.max(
                1080,
                poolColumnWidth +
                  openColumnWidth +
                  displayedColumns.reduce((sum, column) => sum + columnWidths[column.value], 0),
              ),
            }}
          >
            <colgroup>
              <col className="pool-column" />
              {displayedColumns.map((column) => (
                <col key={column.value} style={{ width: columnWidths[column.value] }} />
              ))}
              <col className="open-column" />
            </colgroup>
            <thead>
              <tr>
                <th>Pool</th>
                {displayedColumns.map((column) => (
                  <th
                    key={column.value}
                    aria-sort={
                      column.sort
                        ? sort === column.sort
                          ? direction === "desc"
                            ? "descending"
                            : "ascending"
                          : "none"
                        : undefined
                    }
                  >
                    <span
                      className={
                        column.value === "yieldTvl" || column.value === "volume"
                          ? "yield-sort-control"
                          : undefined
                      }
                    >
                      {column.sort ? (
                        <button
                          onClick={() => {
                            if (column.sort) changeSort(column.sort);
                          }}
                          aria-label={`Sort by ${column.label}`}
                          title={
                            column.value === "yieldTvl"
                              ? "LP fees after protocol share + rewards / TVL"
                              : undefined
                          }
                        >
                          {column.label}{" "}
                          {sort === column.sort ? (direction === "desc" ? "↓" : "↑") : "↕"}
                          <small>
                            {column.value === "volume"
                              ? volumeLabel(volumePeriod)
                              : column.value === "fees" || column.value === "rewards"
                                ? period.toUpperCase()
                                : ""}
                          </small>
                        </button>
                      ) : (
                        <span>{column.label}</span>
                      )}
                      {column.value === "yieldTvl" && (
                        <select
                          aria-label="Yield / TVL period"
                          value={yieldPeriod}
                          onChange={(event) => {
                            changeYieldPeriod(event.target.value as YieldPeriod);
                          }}
                        >
                          {yieldPeriods.map((value) => (
                            <option key={value} value={value}>
                              {value === "15m" || value === "30m" ? value : value.toUpperCase()}
                            </option>
                          ))}
                        </select>
                      )}
                      {column.value === "volume" && (
                        <select
                          aria-label="Volume period"
                          value={volumePeriod}
                          onChange={(event) => {
                            setVolumePeriod(event.target.value as VolumePeriod);
                          }}
                        >
                          {volumePeriods.map((value) => (
                            <option key={value} value={value}>
                              {volumeLabel(value)}
                            </option>
                          ))}
                        </select>
                      )}
                    </span>
                  </th>
                ))}
                <th>
                  <span className="sr-only">Open</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading &&
                !result &&
                Array.from({ length: 5 }, (_, index) => (
                  <tr className="skeleton-row" key={index}>
                    {Array.from({ length: columnCount }, (_, column) => (
                      <td key={column}>
                        <span />
                      </td>
                    ))}
                  </tr>
                ))}
              {result?.items.map((pool) => (
                <PoolRow
                  key={pool.address}
                  pool={withTrend(pool, trends)}
                  busy={loading}
                  columns={displayedColumns.map((column) => column.value)}
                  effectiveLpsCoordinator={effectiveLpsCoordinator}
                  effectiveLpsGeneration={effectiveLpsGeneration}
                  now={now}
                  onOpen={(origin) => {
                    onOpen(pool, result.updatedAt, origin);
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
        {!loading && result?.items.length === 0 && (
          <div className="empty-state">
            <b>{result.nextCursor ? "No matches on this page" : "No pools found"}</b>
            <span>
              Change the search or filters. Missing statistics are never displayed as zero.
            </span>
          </div>
        )}
        <footer className="catalog-footer">
          <div className="catalog-footer-context">
            {yieldPeriod === "365d" && (
              <p className="yield-estimate-note">
                365D Yield / TVL is an annual estimate based on daily yields, not realized one-year
                performance. Market stats are not a promise of position returns.
              </p>
            )}
            <details className="coverage">
              <summary>
                Catalog coverage · Fees {period.toUpperCase()} · Volume {volumeLabel(volumePeriod)}·
                Yield {yieldPeriod.toUpperCase()}
                {loading ? " · Loading catalog information" : error ? " · Refresh unavailable" : ""}
              </summary>
              <p>
                {snapshot ? (
                  <>
                    Catalog coverage: {snapshot.response.coverage || "not specified by the API"}.{" "}
                    Values use the exact{" "}
                    {new URLSearchParams(snapshot.queryIdentity).get("period")?.toUpperCase()} fee
                    and reward period, the{" "}
                    {volumeLabel(
                      new URLSearchParams(snapshot.queryIdentity).get("volumePeriod") ?? "",
                    )}{" "}
                    volume period, and the{" "}
                    {new URLSearchParams(snapshot.queryIdentity).get("yieldPeriod")?.toUpperCase()}{" "}
                    yield period.
                    {snapshot.queryIdentity !== queryIdentity &&
                      " Last loaded coverage; selected request is pending or unavailable."}
                  </>
                ) : error ? (
                  "Catalog information unavailable."
                ) : (
                  "Loading catalog information."
                )}
              </p>
            </details>
          </div>
          {result?.nextCursor && (
            <button
              className="button ghost load-more"
              disabled={loading || moreLoading}
              onClick={() => void loadMore()}
            >
              {moreLoading ? "Loading…" : "Load more"}
            </button>
          )}
        </footer>
      </section>
    </>
  );
}

function PoolRow({
  pool,
  busy,
  columns,
  onOpen,
  effectiveLpsCoordinator,
  effectiveLpsGeneration,
  now,
}: {
  pool: Pool;
  busy: boolean;
  columns: CatalogColumn[];
  onOpen: (origin: HTMLButtonElement) => void;
  effectiveLpsCoordinator: EffectiveLpsCoordinator;
  effectiveLpsGeneration: number;
  now: number;
}) {
  const frame = priceFrame(pool.tokenA, pool.tokenB);
  const autopilot = useAutopilot();
  const notOpenable = openableReason(pool);
  return (
    <tr>
      <td>
        <div className="pool-identity">
          <TokenPair frame={frame} />
          <span className="pool-name-block">
            <span className="pool-name-line">
              <b>
                <TokenPairName frame={frame} />
              </b>
              <PoolDetails pool={pool} />
            </span>
            <span className="pool-address-line">
              <span className="num" title={pool.address}>
                {shorten(pool.address, 6)}
              </span>
              <a
                className="pool-link"
                href={dexPoolUrl(pool.dex, pool.address)}
                target="_blank"
                rel="noreferrer"
                aria-label={`View ${frame.pair} pool on ${dexName(pool.dex)}`}
              >
                <svg viewBox="0 0 12 12" aria-hidden="true">
                  <path d="M4.5 2.5h5v5" />
                  <path d="M9.5 2.5 3 9" />
                </svg>
              </a>
              <DexBadge dex={pool.dex} />
            </span>
            {notOpenable && <span className="pool-openable-reason">{notOpenable}</span>}
            <span className="sr-only">Pool address {pool.address}</span>
          </span>
        </div>
      </td>
      {columns.map((column) => {
        switch (column) {
          case "priceTrend":
            return (
              <td key={column}>
                <PriceTrend pool={pool} />
              </td>
            );
          case "tokenA":
            return (
              <td key={column}>
                <MintIdentity symbol={pool.tokenA.symbol} mint={pool.tokenA.mint} />
              </td>
            );
          case "tokenB":
            return (
              <td key={column}>
                <MintIdentity symbol={pool.tokenB.symbol} mint={pool.tokenB.mint} />
              </td>
            );
          case "yieldTvl":
            return (
              <td
                key={column}
                className={
                  pool.yieldTvlPercent === null || pool.yieldTvlPercent === undefined
                    ? "unavailable"
                    : ""
                }
              >
                {pool.yieldTvlPercent === null || pool.yieldTvlPercent === undefined
                  ? "—"
                  : formatPercent(pool.yieldTvlPercent)}
              </td>
            );
          case "volume":
            return (
              <td key={column} className={pool.volumeUsd === null ? "unavailable" : ""}>
                {pool.volumeUsd === null ? "—" : formatMoney(pool.volumeUsd)}
              </td>
            );
          case "tvl":
            return (
              <td key={column} className={pool.tvlUsd === null ? "unavailable" : ""}>
                {pool.tvlUsd === null ? "—" : formatMoney(pool.tvlUsd)}
              </td>
            );
          case "fees":
            return (
              <td key={column} className={pool.feesUsd === null ? "unavailable" : "accent-value"}>
                {pool.feesUsd === null ? "—" : formatMoney(pool.feesUsd)}
              </td>
            );
          case "effectiveLps":
            return (
              <td key={column}>
                <EffectiveLpsCell
                  address={pool.address}
                  coordinator={effectiveLpsCoordinator}
                  generation={effectiveLpsGeneration}
                  now={now}
                />
              </td>
            );
          case "rewards":
            return (
              <td key={column} className={pool.rewardsUsd === null ? "unavailable" : ""}>
                {pool.rewardsUsd === null ? "—" : formatMoney(pool.rewardsUsd)}
              </td>
            );
          case "price": {
            const displayPrice = frame.priceOrNull(pool.price);
            return (
              <td key={column} className={displayPrice === null ? "unavailable" : ""}>
                {displayPrice === null ? "—" : formatPrice(displayPrice)}
              </td>
            );
          }
          case "address":
            return (
              <td key={column}>
                <a
                  className="pool-link address-cell"
                  href={dexPoolUrl(pool.dex, pool.address)}
                  target="_blank"
                  rel="noreferrer"
                  title={pool.address}
                >
                  {shorten(pool.address, 8)}
                </a>
              </td>
            );
        }
      })}
      <td>
        <button
          className="button primary small"
          data-open-pool={pool.address}
          onClick={(event) => {
            onOpen(event.currentTarget);
          }}
          disabled={
            busy || pool.price === null || autopilot.blocksManualOpening || notOpenable !== null
          }
          title={
            autopilot.blocksManualOpening
              ? AUTOPILOT_MANUAL_OPEN_BLOCKED_REASON
              : (notOpenable ?? (pool.price === null ? "Current price unavailable" : undefined))
          }
        >
          Open
        </button>
      </td>
    </tr>
  );
}

function MintIdentity({ symbol, mint }: { symbol: string; mint: string }) {
  return (
    <a
      className="mint-identity"
      href={`/api/tokens/${mint}/link`}
      target="_blank"
      rel="noreferrer"
      title={mint}
    >
      <b>{symbol}</b>
      <code>{mint}</code>
    </a>
  );
}

function AppliedFilters({
  filters,
  onRemove,
}: {
  filters: CatalogFilters;
  onRemove: (filter: keyof CatalogFilters) => void;
}) {
  const applied: { key: keyof CatalogFilters; label: string }[] = [];
  if (filters.minTvl) applied.push({ key: "minTvl", label: `TVL ≥ $${filters.minTvl}` });
  if (filters.minVolume)
    applied.push({ key: "minVolume", label: `24H volume ≥ $${filters.minVolume}` });
  if (filters.noWarnings) applied.push({ key: "noWarnings", label: "No warnings" });
  if (filters.hasRewards) applied.push({ key: "hasRewards", label: "Has rewards" });
  if (filters.withoutMemes) applied.push({ key: "withoutMemes", label: "Without memes" });
  if (filters.hasAdaptiveFee) applied.push({ key: "hasAdaptiveFee", label: "Adaptive fee" });
  if (filters.isWavebreak) applied.push({ key: "isWavebreak", label: "Wavebreak" });
  if (filters.hasLockedLiquidity)
    applied.push({
      key: "hasLockedLiquidity",
      label: `Locked liquidity ≥ ${filters.minLockedLiquidityPercent}%`,
    });
  if (applied.length === 0) return null;
  return (
    <div className="applied-filters" aria-label="Applied filters">
      {applied.map((filter) => (
        <button
          key={filter.key}
          type="button"
          onClick={() => {
            onRemove(filter.key);
          }}
          aria-label={`Remove filter: ${filter.label}`}
        >
          <span>{filter.label}</span>
          <span aria-hidden="true">×</span>
        </button>
      ))}
    </div>
  );
}
